import React from 'react';
import type { OutboundStage } from '@/services/outboundWorkflowService';

const HAPPY = new Set(['completed', 'accepted']);

function labelFor(code: string | null, stages: OutboundStage[]): string {
  if (!code) return '—';
  return stages.find((s) => s.code === code)?.name || code;
}

/** Default path: completed / accepted only — the list you run if nobody replies. */
export function happyPath(stages: OutboundStage[]): OutboundStage[] {
  const byCode = new Map(stages.map((s) => [s.code, s]));
  const start = stages.find((s) => s.code === 'up_next') || stages[0];
  if (!start) return [];
  const path: OutboundStage[] = [start];
  const seen = new Set([start.code]);
  let current = start;
  while (path.length < 12) {
    const nextCode = (current.exits || []).find((e) => HAPPY.has(e.disposition))?.next_stage_code;
    if (!nextCode || seen.has(nextCode)) break;
    const next = byCode.get(nextCode);
    if (!next) break;
    path.push(next);
    seen.add(next.code);
    current = next;
  }
  return path;
}

export const OutboundWorkflowFlow: React.FC<{ stages: OutboundStage[] }> = ({ stages }) => {
  const path = happyPath(stages);
  if (path.length === 0) return null;
  return (
    <section className="rounded border border-[var(--color-border)] p-3 space-y-2">
      <p className="text-sm text-[var(--color-surface-text)]">
        T-MOS does not guess the next step from list order. Each stage has outcomes (sent, interested,
        nurture). Completing a Today row looks up that outcome and moves the person. Delay days wait
        before the next step is due.
      </p>
      <p className="text-xs font-medium">Default path (no reply)</p>
      <ol className="flex flex-wrap items-center gap-1 text-xs">
        {path.map((s, i) => (
          <li key={s.id} className="inline-flex items-center gap-1">
            {i > 0 && <span aria-hidden="true">→</span>}
            <span className="rounded px-2 py-0.5 bg-[var(--color-primary-bg)] text-[var(--color-secondary-font)]">
              {s.name}
              {s.delay_after_complete_days != null ? ` (+${s.delay_after_complete_days}d)` : ''}
            </span>
          </li>
        ))}
      </ol>
    </section>
  );
};

export const StageExits: React.FC<{ stage: OutboundStage; stages: OutboundStage[] }> = ({
  stage,
  stages,
}) => {
  const exits = stage.exits || [];
  if (exits.length === 0) {
    return <p className="text-xs opacity-70">No outcomes — this is an end state.</p>;
  }
  return (
    <ul className="text-xs space-y-0.5">
      {exits.map((e) => (
        <li key={e.disposition}>
          <span className="font-medium">{e.disposition.replace(/_/g, ' ')}</span>
          {' → '}
          {labelFor(e.next_stage_code, stages)}
          <span className="opacity-70"> ({e.next_status})</span>
        </li>
      ))}
    </ul>
  );
};
